<template>
    <div ref="message" class="py-20 pt-3 pb-3 grid grid-cols-12 gap-x-5">
        <div class="col-span-2 -mb-4">
            <img
                :src="employeePicture"
                class="rounded-full mx-auto w-full aspect-square object-cover"
                alt="Renee van Team4Animation"
            />
            <div class="text-gray font-semibold text-center" v-text="employeeName"></div>
        </div>
        <div class="col-span-12 md:col-span-10">
            <div v-if="showInitialBubble" class="bg-blue text-white before:content-none py-5 px-3">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <p class="mb-5" v-text="chatText"></p>
                <div class="flex justify-between flex-col md:flex-row">
                    <button
                        v-show="isEighteen"
                        class="bg-white border-none font-semibold py-0.5 px-6 inline-block text-blue cursor-pointer transition duration-200 outline-none hover:text-blue-600 text-lg flex-shrink-0 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                        type="button"
                        @click.stop="openEighteenBubble"
                        v-text="snippets.form_snippets_application_form_not_eighteen"
                    ></button>
                    <button
                        v-show="isEighteen"
                        class="bg-white border-none font-semibold py-0.5 px-6 inline-block text-blue cursor-pointer transition duration-200 outline-none hover:text-blue-600 text-lg flex-shrink-0 mt-3 md:mt-0 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                        type="button"
                        @click.stop="closeChat"
                        v-text="snippets.form_snippets_application_form_close_message"
                    ></button>
                </div>
            </div>

            <div v-if="!isEighteen" class="text-white py-3 px-5 relative z-20 my-4 bg-blue-400 ml-auto w-max">
                <span v-text="snippets.form_snippets_application_form_not_eighteen"></span>
            </div>

            <div v-if="!isEighteen" class="bg-blue text-white p-6 relative z-20">
                <p v-text="snippets.form_snippets_application_form_summer_eighteen"></p>
                <div v-show="!selectedYear" class="flex flex-wrap">
                    <button
                        v-for="(year, index) in nextYears"
                        :key="index"
                        class="bg-white border-none font-semibold py-0.5 px-6 inline-block text-blue cursor-pointer transition duration-200 outline-none hover:text-blue-600 text-base flex-shrink-0 mr-1.5 mb-2 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                        type="button"
                        @click="selectedYear = year"
                        v-text="year"
                    ></button>
                    <button
                        class="bg-white border-none font-semibold py-0.5 px-6 inline-block text-blue cursor-pointer transition duration-200 outline-none hover:text-blue-600 text-base flex-shrink-0 mt-4 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                        type="button"
                        @click.stop="closeEighteenBubble"
                        v-text="snippets.form_snippets_application_form_is_eighteen"
                    ></button>
                </div>
            </div>

            <div
                v-if="!isEighteen && selectedYear"
                class="text-white py-3 px-5 relative z-20 my-4 bg-blue-400 ml-auto w-max"
            >
                <span v-text="selectedYear"></span>
            </div>

            <div
                v-if="isTyping"
                class="bg-blue text-white tracking-normal py-6 px-12 text-sm mt-4 inline-block mr-auto"
            >
                <span class="zj-message__typing-dot">.</span>
                <span class="zj-message__typing-dot">.</span>
                <span class="zj-message__typing-dot">.</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, getCurrentInstance, onMounted, inject } from "vue";

const snippets = inject("statamic_snippets");

const { emit } = getCurrentInstance();

const emits = defineEmits(["input", "year-selected"]);

const props = defineProps({
    isAdult: {
        type: Boolean,
        default: false,
    },
    employeePicture: {
        type: String,
        default: "",
    },
    employeeName: {
        type: String,
        default: "",
    },
    employeeMessageSoundUrl: {
        type: String,
        default: "",
    },
    chatText: {
        type: String,
        default: "",
    },
    chatTextEighteen: {
        type: String,
        default: "",
    },
    buttonLeft: {
        type: String,
        default: "",
    },
    buttonRight: {
        type: String,
        default: "",
    },
});

const isTyping = ref(true);
const showInitialBubble = ref(false);
const isEighteen = ref(true);
const selectedYear = ref(null);
const nextYears = ref([
    new Date().getFullYear() + 1,
    new Date().getFullYear() + 2,
    new Date().getFullYear() + 3,
    new Date().getFullYear() + 4,
]);

const message = ref(null);

watch(isEighteen, (isAdult) => {
    emit("input", isAdult);
});

watch(selectedYear, (selectedYear) => {
    emit("year-selected", selectedYear);
});

const stopTyping = () => {
    isTyping.value = false;
};

const playMessageSound = () => {
    const file = props.employeeMessageSoundUrl;
    const audio = new Audio(file);
    audio.play();
    stopTyping();
};

const startInitialChatBubble = () => {
    const observer = new IntersectionObserver((entries, observer) => {
        if (entries[0].isIntersecting) {
            setTimeout(() => {
                stopTyping();
                showInitialBubble.value = true;
                playMessageSound();
            }, 2000);
            observer.unobserve(message.value);
        }
    });

    // Start a observer so the bubble is only seen when the user has this part in it's viewport
    observer.observe(message.value);
};
const closeChat = () => {
    emit("close-chat");
};
const closeEighteenBubble = () => {
    isEighteen.value = true;
    selectedYear.value = null;
};

const startTyping = () => {
    isTyping.value = true;
};

const openEighteenBubble = () => {
    startTyping();
    setTimeout(() => {
        isEighteen.value = false;
        playMessageSound();
    }, 500);
};

onMounted(() => {
    startInitialChatBubble();
});
</script>
